import type { ReactNode } from 'react';
import { Link, useRoute } from 'wouter';
import { useDrafts, type Draft } from '../lib/drafts';
import { stripBoldMarkers } from '../lib/textUtils';
import { ProjectHeader } from '../components/ProjectHeader';
import { NextStep } from '../components/NextStep';

type Column = {
  key: Draft['status'];
  label: string;
  pillClass: string;
  helper: string;
};

const COLUMNS: Column[] = [
  { key: 'pending', label: 'Needs review', pillClass: 'status-pill-amber', helper: 'Fresh from your content team.' },
  { key: 'approved', label: 'Ready to publish', pillClass: 'status-pill-cyan', helper: 'Approved and ready for manual posting.' },
  { key: 'posted', label: 'Published', pillClass: 'status-pill-emerald', helper: 'Live posts with URLs recorded.' },
  { key: 'rejected', label: 'Rejected', pillClass: 'status-pill-red', helper: 'Rejected, but kept for learning.' },
];

export function DraftsPage() {
  const [, paramsPosts] = useRoute('/projects/:id/posts');
  const [, paramsLegacy] = useRoute('/projects/:id/drafts');
  const projectId = (paramsPosts ?? paramsLegacy)?.id;
  const drafts = useDrafts(projectId, 'all');

  if (!projectId) return null;

  const grouped: Record<Draft['status'], Draft[]> = {
    pending: [], approved: [], posted: [], rejected: [],
  };
  (drafts.data ?? []).forEach((d) => grouped[d.status].push(d));

  const total = drafts.data?.length ?? 0;
  const pending = grouped.pending.length;

  return (
    <div className="min-h-full">
      <ProjectHeader projectId={projectId} page="POSTS" />
      <main className="max-w-[1500px] mx-auto px-4 md:px-6 py-4 md:py-8 space-y-6">
        <NextStep projectId={projectId} />
        <section className="rounded-[1.75rem] border border-border-color bg-surface p-4 md:p-6 overflow-hidden relative">
          <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-primary/10 to-transparent pointer-events-none" />
          <div className="relative flex items-end justify-between gap-5 flex-wrap">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-accent-cyan mb-3">Posts</p>
              <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight">Posts ready to review.</h1>
              <p className="mt-3 text-sm text-muted max-w-2xl leading-relaxed">
                Each post includes the visuals and per-channel copy your content team has prepared. Approve, download, and publish manually.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 min-w-[260px]">
              <MiniStat label="Total" value={total} />
              <MiniStat label="Review" value={pending} accent={pending > 0} />
            </div>
          </div>
        </section>

        {drafts.isLoading && <p className="font-mono text-xs uppercase tracking-wider text-muted">Loading posts…</p>}

        {drafts.data && drafts.data.length === 0 && (
          <div className="rounded-[1.5rem] border border-dashed border-border-color p-12 text-center max-w-lg mx-auto bg-surface/60">
            <p className="font-mono text-xs uppercase tracking-wider text-muted mb-4">No posts yet</p>
            <p className="text-sm text-muted leading-relaxed">
              Open <span className="font-mono text-accent-cyan">your content team</span> to brief the first post. It will land here for review.
            </p>
          </div>
        )}

        {drafts.data && drafts.data.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {COLUMNS.map((col) => (
              <PostColumn key={col.key} column={col} drafts={grouped[col.key]} projectId={projectId} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function MiniStat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="rounded-2xl border border-border-color bg-background-dark/70 p-4">
      <p className="font-mono text-[9px] uppercase tracking-wider text-muted">{label}</p>
      <p className={`mt-1 font-display text-2xl md:text-3xl font-bold ${accent ? 'text-accent-amber' : ''}`}>{value}</p>
    </div>
  );
}

function PostColumn({ column, drafts, projectId }: { column: Column; drafts: Draft[]; projectId: string }) {
  return (
    <section className="min-w-0 rounded-[1.25rem] border border-border-color bg-surface/70 p-3">
      <header className="p-2 mb-2">
        <div className="flex items-center justify-between gap-2">
          <p className="font-display text-lg font-bold">{column.label}</p>
          <span className={`status-pill ${column.pillClass}`}>{drafts.length}</span>
        </div>
        <p className="mt-1 text-xs text-muted leading-relaxed">{column.helper}</p>
      </header>
      <div className="space-y-2 min-h-[100px]">
        {drafts.length === 0 && <p className="font-mono text-[10px] uppercase tracking-wider text-muted/60 italic p-2">No posts here yet</p>}
        {drafts.map((d) => <PostCard key={d.id} draft={d} projectId={projectId} />)}
      </div>
    </section>
  );
}

function PostCard({ draft, projectId }: { draft: Draft; projectId: string }) {
  return (
    <Link href={`/projects/${projectId}/posts/${draft.id}`} className="block rounded-2xl border border-border-color bg-background-dark/70 p-4 hover:border-accent-cyan/70 hover:bg-surface-2/70 transition-colors group">
      <div className="flex items-start justify-between gap-3 mb-3">
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted">Created</p>
        <span className="material-symbols-outlined text-muted group-hover:text-accent-cyan text-[18px]">open_in_new</span>
      </div>
      <p className="font-display text-lg font-bold leading-tight line-clamp-3">{stripBoldMarkers(draft.topicTitle)}</p>
      {draft.angle && <p className="mt-2 text-xs text-muted leading-relaxed line-clamp-2">{draft.angle}</p>}
      <div className="mt-4 flex flex-wrap gap-1.5">
        <Chip>{draft.format.replace('_', ' ')}</Chip>
        {draft.linkedinCaption && <Chip>LinkedIn</Chip>}
        {draft.instagramCaption && <Chip>Instagram</Chip>}
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {draft.empathyVerdict && <GateChip label={draft.empathyVerdict.replace('_', ' ')} ok={draft.empathyVerdict === 'helpful'} />}
        {draft.safetyVerdict && <GateChip label={`safety ${draft.safetyVerdict}`} ok={draft.safetyVerdict === 'pass'} />}
      </div>
      <p className="mt-4 font-mono text-[10px] text-muted/70">{new Date(draft.createdAt).toLocaleString()}</p>
    </Link>
  );
}

function Chip({ children }: { children: ReactNode }) {
  return <span className="rounded-full border border-border-color px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted">{children}</span>;
}

function GateChip({ label, ok }: { label: string; ok: boolean }) {
  return <span className={`status-pill ${ok ? 'status-pill-emerald' : 'status-pill-amber'}`}>{label}</span>;
}
